import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createProject,
  getOptions,
  getProfile,
  previewProject,
  saveProfile,
  type ProjectPreview,
} from '../api';
import type {
  CreateProjectPayload,
  Goal,
  MealPreferences,
  OptionGroup,
  OptionGroups,
  RecompPopulation,
  UserProfile,
} from '../types';
import { Field } from '../components/Field';
import { Select } from '../components/Select';
import { MultiSelect } from '../components/MultiSelect';
import { WeightInput } from '../components/WeightInput';
import { Spinner } from '../components/Spinner';

// ProjectSetup wizard (P5 steps 7b.2.i + 7b.2.ii). Pre-step + Steps 1–7
// wired end-to-end through the live worker preview + create endpoints.
// Direct URL only — no entry point from Dashboard, no BootGate redirect
// yet (Step 7c carryover).

type Form = {
  goal: Goal | null;
  recomp_population: RecompPopulation | null;
  target_weight_kg: number | null;
  fasting_schedule: string | null;
  timeline_weeks: number | null;
  safety_override: boolean;
  meal_preferences: MealPreferences;
};

// prefill stub: profile.per_meal_preferences exists in the DB but is typed
// as Record<string, unknown> | null on UserProfile (untyped JSON), so it
// can't be safely cast to MealPreferences without runtime validation.
// Users start blank for now; revisit when the column is either typed or
// the food-DB lands.
const emptyMealPreferences: MealPreferences = {
  breakfast: { proteins: [], carbs: [], skip: false },
  lunch:     { proteins: [], carbs: [], veg: [] },
  dinner:    { proteins: [], carbs: [], veg: [] },
  snacks:    { items: [], skip: false },
};

const initialForm: Form = {
  goal: null,
  recomp_population: null,
  target_weight_kg: null,
  fasting_schedule: null,
  timeline_weeks: null,
  safety_override: false,
  meal_preferences: emptyMealPreferences,
};

// Long-fast schedules where breakfast is auto-skipped on Step 5 → Step 6
// transition. 20:4 is intentionally excluded — warrior eating windows can
// fall mid-day and shouldn't presume a skipped morning.
const fastingExcludesMorning = (s: string | null): boolean =>
  s === '16_8' || s === '18_6' || s === 'omad';

const GOAL_KEYS = ['fat_loss', 'recomp', 'muscle_gain', 'maintenance'] as const;

// profile.primary_goal → projects.goal vocabulary for wizard prefill (S10).
// improve_fitness and athletic_performance have no clean project shape (no
// calorie deficit/surplus stance) — wizard asks fresh in those cases by
// leaving form.goal at its initial null. No mirror-back: profile.primary_goal
// is profile-overall, projects.goal is project-shape; kept conceptually
// distinct.
const PRIMARY_GOAL_TO_PROJECT_GOAL: Record<string, Goal> = {
  lose_fat: 'fat_loss',
  build_muscle: 'muscle_gain',
  recomp: 'recomp',
  maintain: 'maintenance',
};

const GOAL_EYEBROW: Record<Goal, string> = {
  fat_loss: 'Lose weight',
  recomp: 'Stay the same weight',
  muscle_gain: 'Gain weight',
  maintenance: 'Hold steady',
};

const GOAL_TITLE: Record<Goal, string> = {
  fat_loss: 'Fat loss',
  recomp: 'Body recomposition',
  muscle_gain: 'Muscle gain',
  maintenance: 'Maintenance',
};

const GOAL_BLURB: Record<Goal, string> = {
  fat_loss: 'Calorie deficit. Sustainable rate, no crash.',
  recomp: 'Roughly the same weight, less fat, more muscle. Slow.',
  muscle_gain: 'Calorie surplus. Lean gains, minimal fat.',
  maintenance: 'Hold current weight. Habit-building mode.',
};

// recomp_population picker. Inlined as an OptionGroup so the existing Select
// component renders it without a new variant — same pattern Welcome uses for
// fasting_schedule, just sourced locally instead of from getOptions().
const RECOMP_POPULATION_GROUP: OptionGroup = {
  field: 'recomp_population',
  description: 'Recomp population',
  multi: false,
  allow_other: false,
  options: [
    { value: 'beginner', label: 'New to lifting (under 1 year consistent)' },
    { value: 'returning', label: 'Returning after a long break' },
    { value: 'high_bf', label: 'Higher body fat (above ~25% M / ~32% F)' },
    { value: 'override', label: 'None of these — override anyway' },
  ],
};

const KG_PER_LB = 0.45359237;
const fmtRate = (weeklyKg: number, unit: 'metric' | 'imperial'): string => {
  if (unit === 'imperial') return `${(weeklyKg / KG_PER_LB).toFixed(2)} lb/week`;
  return `${weeklyKg.toFixed(2)} kg/week`;
};
const fmtWeight = (kg: number, unit: 'metric' | 'imperial'): string => {
  if (unit === 'imperial') return `${(kg / KG_PER_LB).toFixed(1)} lb`;
  return `${kg.toFixed(1)} kg`;
};

export function ProjectSetup() {
  // 0 = pre-step (activity_level), 1–7 = wizard steps.
  const [step, setStep] = useState<number>(0);
  const [form, setForm] = useState<Form>(initialForm);
  const [groups, setGroups] = useState<OptionGroups | null>(null);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [preview, setPreview] = useState<ProjectPreview | null>(null);
  const [previewing, setPreviewing] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Step 6 sub-screen — one of breakfast/lunch/dinner/snacks. Set on entry
  // from Step 5 (continueToMealPrefs) based on fasting schedule.
  const [mealStep, setMealStep] = useState<'breakfast' | 'lunch' | 'dinner' | 'snacks'>('breakfast');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Set when POST /projects returns 409 active_project_exists — the
  // submitError carries the worker's message, this carries the blocking
  // project's id so the "Go to dashboard" CTA can hand it to Dashboard via
  // router state to auto-open the cancel-confirm modal.
  const [blockedActiveProjectId, setBlockedActiveProjectId] = useState<string | null>(null);
  const navigate = useNavigate();

  const set = <K extends keyof Form>(key: K, value: Form[K]): void =>
    setForm((f) => ({ ...f, [key]: value }));

  // Shared Step 5 → Step 6 entry. Centralises:
  //   1. setting safety_override (warn-Continue-anyway path)
  //   2. auto-skipping breakfast for long fasts (16:8 / 18:6 / OMAD)
  //   3. choosing the starting sub-screen
  // Both Step 5 forward buttons (safe Continue, warn Continue anyway) call
  // this — keeps the sub-step routing consistent across both entry paths.
  const continueToMealPrefs = (overrideSafety: boolean): void => {
    if (overrideSafety) set('safety_override', true);
    if (fastingExcludesMorning(form.fasting_schedule)) {
      setForm((f) => ({
        ...f,
        meal_preferences: {
          ...f.meal_preferences,
          breakfast: { ...f.meal_preferences.breakfast, skip: true },
        },
      }));
      setMealStep('lunch');
    } else {
      setMealStep('breakfast');
    }
    setStep(6);
  };

  // Submit handler — POSTs the full project payload. 201 → /dashboard,
  // 422 → re-render Step 5 with new gate state. The 422 path is only
  // reachable via a profile-mutation race (e.g. user changed weight in
  // another tab between preview and submit) because once safety_override
  // is true the worker bypasses the gate; defensive handling either way.
  const handleSubmit = async (): Promise<void> => {
    if (!form.goal) return;
    setSubmitting(true);
    setSubmitError(null);
    setBlockedActiveProjectId(null);
    try {
      const payload: CreateProjectPayload = {
        goal: form.goal,
        target_weight_kg: form.target_weight_kg,
        timeline_weeks: form.timeline_weeks,
        fasting_schedule: form.fasting_schedule,
        meal_preferences: form.meal_preferences,
        recomp_population: form.recomp_population,
        safety_override: form.safety_override,
      };
      const result = await createProject(payload);
      if (result.saved) {
        navigate('/dashboard');
        return;
      }
      // 409 — user already has an active project. Shares saved:false with
      // the gate arm; `kind` disambiguates. Surface message in the errorbar;
      // the "Go to dashboard" CTA below navigates with router state so
      // Dashboard auto-opens the cancel-confirm modal scoped to the id.
      if ('kind' in result && result.kind === 'active_project_exists') {
        setSubmitError(result.message);
        setBlockedActiveProjectId(result.activeProjectId);
        return;
      }
      // 422 — gate-failed. Re-use cached preview.targets (worker doesn't
      // return targets on the gate path), refresh safety from the response.
      // `'safety' in result` narrows ProjectGated | ProjectActiveExists down
      // to ProjectGated; `'kind' in result` only narrows the truthy branch.
      if (preview && 'safety' in result) {
        setPreview({ safety: result.safety, targets: preview.targets });
      }
      setStep(5);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([getOptions(), getProfile()])
      .then(([g, p]) => {
        if (cancelled) return;
        setGroups(g);
        setProfile(p);
        if (p) {
          // Prefill four wizard fields from profile (S10/S12) — prefill-with-edit,
          // not skip-step, because users may legitimately diverge between
          // profile-overall and project-specific values. The maintenance
          // branch on target_weight_kg mirrors Step 1's onChange invariant
          // (goal === 'maintenance' implies target_weight_kg === null) so
          // prefill doesn't slip a non-null target past the wizard's skip
          // logic and into a backend-rejected payload.
          const mappedGoal = p.primary_goal
            ? PRIMARY_GOAL_TO_PROJECT_GOAL[p.primary_goal] ?? null
            : null;
          setForm((f) => ({
            ...f,
            goal: mappedGoal ?? f.goal,
            target_weight_kg: mappedGoal === 'maintenance'
              ? null
              : (p.target_weight_kg ?? f.target_weight_kg),
            fasting_schedule: p.fasting_schedule ?? f.fasting_schedule,
            recomp_population: mappedGoal === 'recomp'
              ? (p.recomp_population ?? f.recomp_population)
              : null,
          }));
        }
        // Skip the pre-step when activity_level is already on file. BootGate
        // routes incomplete-profile users elsewhere, so a profile with sex
        // set is expected here; the activity_level guard handles the case
        // where a user filled Welcome before activity_level was required.
        if (p && p.activity_level) setStep(1);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setGroupsError(err instanceof Error ? err.message : 'Failed to load');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Centralised preview fetch. Step 3's maintenance branch and Step 4's Next
  // both call this — keeps Step 5's render consistent regardless of which
  // path reached it. Returns true on success so the caller can advance.
  const runPreview = async (next: Form): Promise<boolean> => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const result = await previewProject({
        goal: next.goal!,
        target_weight_kg: next.target_weight_kg,
        timeline_weeks: next.timeline_weeks,
        recomp_population: next.recomp_population,
      });
      setPreview(result);
      return true;
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Preview failed');
      return false;
    } finally {
      setPreviewing(false);
    }
  };

  if (groupsError) {
    return (
      <div className="shell">
        <div className="errorbar">{groupsError}</div>
      </div>
    );
  }
  if (!groups || !profile) {
    return <div className="center"><Spinner /></div>;
  }

  const unit = profile.unit_preference ?? 'metric';
  const currentKg = profile.weight_kg ?? 0;

  // Inline styles — kept off the global stylesheet because they're throwaway
  // wizard chrome (one-off layout). The only net-new CSS class for 7b.2.i is
  // .card--selected; everything else reuses existing tokens.
  const dotsStyle: CSSProperties = {
    display: 'flex',
    gap: 8,
    justifyContent: 'center',
    marginBottom: 20,
    color: 'var(--text-faint)',
    fontSize: 14,
  };
  const actionsRow: CSSProperties = {
    display: 'flex',
    gap: 8,
    justifyContent: 'space-between',
    marginTop: 16,
  };

  return (
    <form className="shell" onSubmit={(e) => e.preventDefault()}>
      {step > 0 ? (
        <div aria-hidden="true" style={dotsStyle}>
          {[1, 2, 3, 4, 5, 6, 7].map((n) => (
            <span key={n} style={{ opacity: n <= step ? 1 : 0.4 }}>
              {n <= step ? '●' : '○'}
            </span>
          ))}
        </div>
      ) : null}

      {step === 0 ? (
        <section className="section rise">
          <h2 className="h2">Quick question first</h2>
          <p className="muted">We need your typical activity level to size your daily targets.</p>
          {groups.activity_level ? (
            <Select
              label="Activity level"
              group={groups.activity_level}
              value={null}
              onChange={async (v) => {
                if (!v) return;
                try {
                  await saveProfile({ activity_level: v });
                  setProfile({ ...profile, activity_level: v });
                  setStep(1);
                } catch (err) {
                  setGroupsError(err instanceof Error ? err.message : 'Failed to save activity level');
                }
              }}
            />
          ) : null}
        </section>
      ) : null}

      {step === 1 ? (
        <section className="section rise">
          <h2 className="h2">What are you here for?</h2>
          <div className="grid-2">
            {GOAL_KEYS.map((g) => (
              <label
                key={g}
                className={`card card--interactive${form.goal === g ? ' card--selected' : ''}`}
              >
                {/* Native radio inside a label — mirrors Welcome's
                    biological-sex pattern. Visual chrome lives on the
                    parent label; .toggle__radio visually-hides the input
                    while keeping it focusable. Avoids the iOS Safari
                    aria-pressed flakiness documented in Welcome.tsx. */}
                <input
                  type="radio"
                  name="goal"
                  value={g}
                  className="toggle__radio"
                  checked={form.goal === g}
                  onChange={() => {
                    setForm((f) => ({
                      ...f,
                      goal: g,
                      // Reset goal-conditional fields whenever goal changes
                      // so stale values don't leak between selections.
                      recomp_population: g === 'recomp' ? f.recomp_population : null,
                      target_weight_kg: g === 'maintenance' ? null : f.target_weight_kg,
                      timeline_weeks: g === 'maintenance' ? null : f.timeline_weeks,
                    }));
                  }}
                />
                <p className="eyebrow">{GOAL_EYEBROW[g]}</p>
                <p className="h3" style={{ marginTop: 4 }}>{GOAL_TITLE[g]}</p>
                <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>{GOAL_BLURB[g]}</p>
              </label>
            ))}
          </div>

          {form.goal === 'recomp' ? (
            <Select
              label="Which best describes you?"
              group={RECOMP_POPULATION_GROUP}
              value={form.recomp_population}
              onChange={(v) => set('recomp_population', v as RecompPopulation | null)}
              hint="Recomp only realistically works for one of these. Picking 'None' lets you override the gate."
            />
          ) : null}

          <div style={actionsRow}>
            <span />
            <button
              type="button"
              className="btn btn--primary"
              disabled={!form.goal || (form.goal === 'recomp' && !form.recomp_population)}
              onClick={() => setStep(form.goal === 'maintenance' ? 3 : 2)}
            >
              Next
            </button>
          </div>
        </section>
      ) : null}

      {step === 2 ? (() => {
        // Direction-coherence with the chosen goal, mirroring the worker's
        // safetyReview guard (and its 422). fat_loss must land below current,
        // muscle_gain above; recomp accepts either. Blocking Next here means
        // the contradiction never reaches preview — and it catches a goal-card
        // mis-pick too (e.g. muscle_gain with a lower target → "needs a target
        // above 92 kg", which tells the user they meant fat loss).
        const t = form.target_weight_kg;
        let dirErr: string | null = null;
        if (t !== null && currentKg > 0) {
          if (form.goal === 'fat_loss' && t > currentKg) {
            dirErr = `Fat loss needs a target below your current weight (${fmtWeight(currentKg, unit)}).`;
          } else if (form.goal === 'muscle_gain' && t < currentKg) {
            dirErr = `Muscle gain needs a target above your current weight (${fmtWeight(currentKg, unit)}).`;
          }
        }
        return (
          <section className="section rise">
            <h2 className="h2">Target weight</h2>
            <p className="muted">
              Where you want to land. Your current weight is {fmtWeight(currentKg, unit)}.
            </p>
            <WeightInput
              label="Target weight"
              value_kg={form.target_weight_kg}
              onChange={(v) => set('target_weight_kg', v)}
              unit={unit}
            />
            {dirErr ? <div className="errorbar">{dirErr}</div> : null}
            <div style={actionsRow}>
              <button type="button" className="btn btn--ghost" onClick={() => setStep(1)}>Back</button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={!form.target_weight_kg || dirErr !== null}
                onClick={() => setStep(3)}
              >
                Next
              </button>
            </div>
          </section>
        );
      })() : null}

      {step === 3 ? (
        <section className="section rise">
          <h2 className="h2">Fasting / eating window</h2>
          {groups.fasting_schedule ? (
            <Select
              label="Eating window"
              group={groups.fasting_schedule}
              value={form.fasting_schedule}
              onChange={(v) => set('fasting_schedule', v)}
              hint="Affects when meals get scheduled."
            />
          ) : null}
          {previewError ? <div className="errorbar">{previewError}</div> : null}
          <div style={actionsRow}>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setStep(form.goal === 'maintenance' ? 1 : 2)}
            >
              Back
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={!form.fasting_schedule || previewing}
              onClick={async () => {
                if (form.goal === 'maintenance') {
                  // Maintenance skips Step 4 — fetch preview here so Step 5
                  // has populated safety/targets. With null target/timeline
                  // the worker hits the maintenance short-circuit (level:
                  // 'safe', kcal at TDEE).
                  const ok = await runPreview(form);
                  if (ok) setStep(5);
                } else {
                  setStep(4);
                }
              }}
            >
              {previewing ? <><Spinner /> Checking…</> : 'Next'}
            </button>
          </div>
        </section>
      ) : null}

      {step === 4 ? (() => {
        const targetKg = form.target_weight_kg ?? currentKg;
        const weeks = form.timeline_weeks ?? 0;
        const weeklyKg = weeks > 0 ? Math.abs((targetKg - currentKg) / weeks) : 0;
        const weeklyPct = weeks > 0 && currentKg > 0
          ? (Math.abs(targetKg - currentKg) / weeks / currentKg) * 100
          : 0;
        return (
          <section className="section rise">
            <h2 className="h2">Timeline</h2>
            <p className="muted">How long do you want to give it?</p>
            <Field label="Weeks" hint="1–104. Worker rejects anything outside that range.">
              <input
                className="input"
                type="number"
                inputMode="numeric"
                min={1}
                max={104}
                step={1}
                value={form.timeline_weeks ?? ''}
                onChange={(e) =>
                  set('timeline_weeks', e.target.value === '' ? null : Number(e.target.value))
                }
              />
            </Field>
            {weeks > 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>
                ≈ {fmtRate(weeklyKg, unit)} ({weeklyPct.toFixed(2)}% bodyweight/week)
              </p>
            ) : null}
            {previewError ? <div className="errorbar">{previewError}</div> : null}
            <div style={actionsRow}>
              <button type="button" className="btn btn--ghost" onClick={() => setStep(3)}>Back</button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={!form.timeline_weeks || form.timeline_weeks < 1 || previewing}
                onClick={async () => {
                  const ok = await runPreview(form);
                  if (ok) setStep(5);
                }}
              >
                {previewing ? <><Spinner /> Checking…</> : 'Next'}
              </button>
            </div>
          </section>
        );
      })() : null}

      {step === 5 && preview ? (() => {
        const s = preview.safety;
        const t = preview.targets;
        const recommended = s.recommended_timeline_weeks;
        const faster = s.faster_safe_weeks;

        // Apply a recommended/minimum/faster timeline and bounce back to
        // Step 4 so the user re-confirms via Next; runPreview re-runs against
        // the new value. Clears stale previewError so Step 4 doesn't render
        // it after a successful adjustment.
        const useTimeline = (weeks: number): void => {
          set('timeline_weeks', weeks);
          setPreviewError(null);
          setStep(4);
        };

        return (
          <section className="section rise">
            <h2 className="h2">Safety check</h2>

            {s.level === 'safe' ? (
              <>
                <div className="card">
                  <p className="eyebrow">Targets</p>
                  <p className="h3" style={{ marginTop: 4 }}>{t.daily_kcal_target} kcal/day</p>
                  <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                    {t.protein_target_g}g protein · {t.carb_target_g}g carbs · {t.fat_target_g}g fat
                  </p>
                  {form.goal !== 'maintenance' ? (
                    <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                      Rate: {fmtRate(Math.abs(s.weekly_delta_kg), unit)}{' '}
                      ({s.weekly_pct_bodyweight.toFixed(2)}% bodyweight/week)
                    </p>
                  ) : null}
                </div>
                {faster !== null ? (
                  <div className="card">
                    <p className="eyebrow">Faster path available</p>
                    <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                      Could safely hit your target in {faster} weeks instead of {form.timeline_weeks}.
                    </p>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      style={{ marginTop: 12 }}
                      onClick={() => useTimeline(faster)}
                    >
                      Use faster timeline
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}

            {s.level === 'warn' ? (
              <>
                <div className="card">
                  <span className="tag tag--alert">Warning</span>
                  <p style={{ marginTop: 8 }}>{s.reason}</p>
                </div>
                {recommended !== null ? (
                  <div className="card">
                    <p className="eyebrow">Recommended</p>
                    <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                      Stretch the timeline to {recommended} weeks for a safer rate.
                    </p>
                    <button
                      type="button"
                      className="btn btn--primary"
                      style={{ marginTop: 12 }}
                      onClick={() => useTimeline(recommended)}
                    >
                      Use recommended
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}

            {s.level === 'gate' ? (
              <>
                <div className="card">
                  <span className="tag tag--alert">Not safe at this rate</span>
                  <p style={{ marginTop: 8 }}>{s.reason}</p>
                </div>
                {recommended !== null ? (
                  <div className="card">
                    <p className="eyebrow">Required minimum</p>
                    <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                      Timeline must be at least {recommended} weeks.
                    </p>
                    <button
                      type="button"
                      className="btn btn--primary"
                      style={{ marginTop: 12 }}
                      onClick={() => useTimeline(recommended)}
                    >
                      Use minimum
                    </button>
                  </div>
                ) : null}
              </>
            ) : null}

            <div style={actionsRow}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setStep(form.goal === 'maintenance' ? 3 : 4)}
              >
                Back
              </button>
              {s.level === 'safe' ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => continueToMealPrefs(false)}
                >
                  Continue
                </button>
              ) : null}
              {s.level === 'warn' ? (
                // No .btn--alert variant exists yet; using plain .btn so the
                // override path is visually quieter than the recommended
                // [Use recommended] primary button. Visual delta can be
                // sorted in a polish pass.
                <button
                  type="button"
                  className="btn"
                  onClick={() => continueToMealPrefs(true)}
                >
                  Continue anyway
                </button>
              ) : null}
              {/* Gate has no forward button — user must change timeline. */}
            </div>
          </section>
        );
      })() : null}

      {step === 6 ? (
        <section className="section rise">
          {mealStep === 'breakfast' ? (
            <>
              <h2 className="h2">Breakfast</h2>
              <p className="muted">Pick what you'll actually eat. We'll build meals around these.</p>
              {groups.meal_proteins ? (
                <MultiSelect
                  label="Proteins"
                  group={groups.meal_proteins}
                  values={form.meal_preferences.breakfast.proteins}
                  onChange={(v) => set('meal_preferences', {
                    ...form.meal_preferences,
                    breakfast: { ...form.meal_preferences.breakfast, proteins: v },
                  })}
                />
              ) : null}
              {groups.meal_carbs ? (
                <MultiSelect
                  label="Carbs"
                  group={groups.meal_carbs}
                  values={form.meal_preferences.breakfast.carbs}
                  onChange={(v) => set('meal_preferences', {
                    ...form.meal_preferences,
                    breakfast: { ...form.meal_preferences.breakfast, carbs: v },
                  })}
                />
              ) : null}
              <div style={actionsRow}>
                <button type="button" className="btn btn--ghost" onClick={() => setStep(5)}>Back</button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => setMealStep('lunch')}
                >
                  Next
                </button>
              </div>
            </>
          ) : null}

          {mealStep === 'lunch' ? (
            <>
              <h2 className="h2">Lunch</h2>
              <p className="muted">Same drill — proteins, carbs, and a veg pick this time.</p>
              {groups.meal_proteins ? (
                <MultiSelect
                  label="Proteins"
                  group={groups.meal_proteins}
                  values={form.meal_preferences.lunch.proteins}
                  onChange={(v) => set('meal_preferences', {
                    ...form.meal_preferences,
                    lunch: { ...form.meal_preferences.lunch, proteins: v },
                  })}
                />
              ) : null}
              {groups.meal_carbs ? (
                <MultiSelect
                  label="Carbs"
                  group={groups.meal_carbs}
                  values={form.meal_preferences.lunch.carbs}
                  onChange={(v) => set('meal_preferences', {
                    ...form.meal_preferences,
                    lunch: { ...form.meal_preferences.lunch, carbs: v },
                  })}
                />
              ) : null}
              {groups.meal_veg ? (
                <MultiSelect
                  label="Vegetables"
                  group={groups.meal_veg}
                  values={form.meal_preferences.lunch.veg}
                  onChange={(v) => set('meal_preferences', {
                    ...form.meal_preferences,
                    lunch: { ...form.meal_preferences.lunch, veg: v },
                  })}
                />
              ) : null}
              <div style={actionsRow}>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    // If breakfast was auto-skipped (long fast), Back escapes
                    // to Step 5 instead of dropping into a skipped sub-screen.
                    if (form.meal_preferences.breakfast.skip) {
                      setStep(5);
                    } else {
                      setMealStep('breakfast');
                    }
                  }}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => setMealStep('dinner')}
                >
                  Next
                </button>
              </div>
            </>
          ) : null}

          {mealStep === 'dinner' ? (
            <>
              <h2 className="h2">Dinner</h2>
              <p className="muted">Last main meal of the day.</p>
              {groups.meal_proteins ? (
                <MultiSelect
                  label="Proteins"
                  group={groups.meal_proteins}
                  values={form.meal_preferences.dinner.proteins}
                  onChange={(v) => set('meal_preferences', {
                    ...form.meal_preferences,
                    dinner: { ...form.meal_preferences.dinner, proteins: v },
                  })}
                />
              ) : null}
              {groups.meal_carbs ? (
                <MultiSelect
                  label="Carbs"
                  group={groups.meal_carbs}
                  values={form.meal_preferences.dinner.carbs}
                  onChange={(v) => set('meal_preferences', {
                    ...form.meal_preferences,
                    dinner: { ...form.meal_preferences.dinner, carbs: v },
                  })}
                />
              ) : null}
              {groups.meal_veg ? (
                <MultiSelect
                  label="Vegetables"
                  group={groups.meal_veg}
                  values={form.meal_preferences.dinner.veg}
                  onChange={(v) => set('meal_preferences', {
                    ...form.meal_preferences,
                    dinner: { ...form.meal_preferences.dinner, veg: v },
                  })}
                />
              ) : null}
              <div style={actionsRow}>
                <button type="button" className="btn btn--ghost" onClick={() => setMealStep('lunch')}>Back</button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => setMealStep('snacks')}
                >
                  Next
                </button>
              </div>
            </>
          ) : null}

          {mealStep === 'snacks' ? (
            <>
              <h2 className="h2">Snacks</h2>
              <p className="muted">Anything you'll reach for between meals.</p>
              {groups.meal_snacks ? (
                <MultiSelect
                  label="Snacks"
                  group={groups.meal_snacks}
                  values={form.meal_preferences.snacks.items}
                  onChange={(v) => set('meal_preferences', {
                    ...form.meal_preferences,
                    snacks: { ...form.meal_preferences.snacks, items: v },
                  })}
                />
              ) : null}
              <div style={actionsRow}>
                <button type="button" className="btn btn--ghost" onClick={() => setMealStep('dinner')}>Back</button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => setStep(7)}
                >
                  Review
                </button>
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      {step === 7 && form.goal ? (
        <section className="section rise">
          <h2 className="h2">Ready to start?</h2>
          <p className="muted">Review your project. You can edit later from the dashboard.</p>

          <div className="card">
            <p className="eyebrow">Goal</p>
            <p className="h3" style={{ marginTop: 4 }}>{GOAL_TITLE[form.goal]}</p>
            {form.goal === 'recomp' && form.recomp_population ? (
              <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                Population: {form.recomp_population}
              </p>
            ) : null}
          </div>

          {form.goal !== 'maintenance' && form.target_weight_kg !== null ? (
            <div className="card">
              <p className="eyebrow">Target</p>
              <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                {fmtWeight(form.target_weight_kg, unit)} in {form.timeline_weeks} weeks
              </p>
            </div>
          ) : null}

          {form.fasting_schedule ? (
            <div className="card">
              <p className="eyebrow">Eating window</p>
              <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>{form.fasting_schedule}</p>
            </div>
          ) : null}

          {preview ? (
            <div className="card">
              <p className="eyebrow">Daily targets</p>
              <p className="h3" style={{ marginTop: 4 }}>{preview.targets.daily_kcal_target} kcal</p>
              <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
                {preview.targets.protein_target_g}g protein · {preview.targets.carb_target_g}g carbs · {preview.targets.fat_target_g}g fat
              </p>
            </div>
          ) : null}

          <div className="card">
            <p className="eyebrow">Meal preferences</p>
            <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
              Breakfast: {form.meal_preferences.breakfast.skip
                ? 'skipped (fasting)'
                : `${form.meal_preferences.breakfast.proteins.length} proteins, ${form.meal_preferences.breakfast.carbs.length} carbs`}
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              Lunch: {form.meal_preferences.lunch.proteins.length} proteins, {form.meal_preferences.lunch.carbs.length} carbs, {form.meal_preferences.lunch.veg.length} veg
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              Dinner: {form.meal_preferences.dinner.proteins.length} proteins, {form.meal_preferences.dinner.carbs.length} carbs, {form.meal_preferences.dinner.veg.length} veg
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              Snacks: {form.meal_preferences.snacks.skip
                ? 'none'
                : `${form.meal_preferences.snacks.items.length} items`}
            </p>
          </div>

          {submitError ? <div className="errorbar">{submitError}</div> : null}
          {blockedActiveProjectId ? (
            <button
              type="button"
              className="btn btn--primary"
              style={{ marginTop: 12 }}
              onClick={() =>
                navigate('/dashboard', {
                  state: { autoConfirmCancelFor: blockedActiveProjectId },
                })
              }
            >
              Go to dashboard
            </button>
          ) : null}

          <div style={actionsRow}>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setStep(6);
                setMealStep('snacks');
              }}
            >
              Back
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? <><Spinner /> Starting…</> : 'Start project'}
            </button>
          </div>
        </section>
      ) : null}
    </form>
  );
}
